/**
 * useGroupChat — 群聊状态管理 Hook
 *
 * 职责：
 * - 管理用户信息初始化（读取 localStorage / 触发弹窗）
 * - 管理消息列表（增删改查）
 * - 维护消息服务适配器（当前为 mock，可替换为 WebSocket）
 * - 处理消息发送状态（sending → sent / failed）
 * - 处理重发逻辑
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type { GroupMessage, MessageServiceAdapter, WSConnectionStatus, ChatUser, ContentSegment } from '../types';
import { createWSAdapter } from '../utils/wsAdapter';
import { loadUser } from '../utils/storage';

export interface UseGroupChatReturn {
  /** 当前用户信息 */
  currentUser: ChatUser | null;
  /** 是否需要显示用户信息弹窗 */
  showUserModal: boolean;
  /** 消息列表 */
  messages: GroupMessage[];
  /** WebSocket 连接状态 */
  connectionStatus: WSConnectionStatus;
  /** 是否正在发送 */
  sending: boolean;
  /** 设置用户信息（从弹窗确认后调用） */
  setUser: (user: { nickname: string; avatar: string }) => void;
  /** 发送消息 */
  sendMessage: (msg: { segments: ContentSegment[] }) => void;
  /** 重发消息 */
  retryMessage: (message: GroupMessage) => void;
}

/** 生成客户端临时消息 ID */
const genTempId = (): string => `temp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

/**
 * 群聊状态管理 Hook
 *
 * 初始化流程：
 * 1. 读取 localStorage 中的用户信息
 * 2. 如有用户信息 → 自动连接消息服务
 * 3. 如无用户信息 → 弹窗 → 确认后连接
 */
export const useGroupChat = (): UseGroupChatReturn => {
  // ==================== 用户状态 ====================

  const [currentUser, setCurrentUser] = useState<ChatUser | null>(() => {
    const stored = loadUser();
    return stored ? { userId: stored.userId, nickname: stored.nickname, avatar: stored.avatar } : null;
  });

  const [showUserModal, setShowUserModal] = useState(() => !loadUser());

  // ==================== 消息状态 ====================

  const [messages, setMessages] = useState<GroupMessage[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<WSConnectionStatus>('disconnected');
  const [sending, setSending] = useState(false);

  /** 消息服务适配器引用 */
  const adapterRef = useRef<MessageServiceAdapter | null>(null);

  // ==================== 设置用户 ====================

  /**
   * 弹窗确认后设置用户信息
   * 用户信息已在 UserModal 中写入 localStorage
   */
  const setUser = useCallback((user: { nickname: string; avatar: string }) => {
    const stored = loadUser();
    if (!stored) return;

    const chatUser: ChatUser = {
      userId: stored.userId,
      nickname: user.nickname,
      avatar: user.avatar,
    };

    setCurrentUser(chatUser);
    setShowUserModal(false);
  }, []);

  // ==================== 初始化消息服务 ====================

  useEffect(() => {
    if (!currentUser) return;

    // 使用 WebSocket 适配器连接真实服务端
    const adapter = createWSAdapter(currentUser.userId, currentUser.nickname, currentUser.avatar);
    adapterRef.current = adapter;

    // 设置回调
    adapter.onStatusChange = (status: WSConnectionStatus) => {
      setConnectionStatus(status);
    };

    adapter.onMessage = (msg: GroupMessage) => {
      setMessages((prev) => {
        // 自己发的消息 → 更新已有的 sending 临时消息，避免重复
        if (msg.senderId === currentUser.userId) {
          const idx = prev.findIndex((m) => m.status === 'sending');
          if (idx !== -1) {
            const updated = [...prev];
            updated[idx] = { ...msg, status: 'sent' };
            return updated;
          }
        }
        // 他人消息 → 追加
        return [...prev, msg];
      });
    };

    // 历史消息加载完成后，替换消息列表
    adapter.onHistoryLoaded = (list: GroupMessage[]) => {
      setMessages(list);
    };

    // 连接
    adapter.connect(currentUser.userId);

    return () => {
      adapter.disconnect();
      adapterRef.current = null;
    };
  }, [currentUser]);

  // ==================== 发送消息 ====================

  /**
   * 发送消息
   * 1. 生成临时消息 ID，加入列表（status: sending）
   * 2. 委托适配器发送
   * 3. 适配器回调会将状态更新为 sent 或 failed
   */
  const sendMessage = useCallback(
    (msg: { segments: ContentSegment[] }) => {
      if (!currentUser || !adapterRef.current) return;

      const tempMsg: GroupMessage = {
        id: genTempId(),
        segments: msg.segments,
        senderId: currentUser.userId,
        senderName: currentUser.nickname,
        senderAvatar: currentUser.avatar,
        timestamp: Date.now(),
        status: 'sending',
      };

      // 加入列表
      setMessages((prev) => [...prev, tempMsg]);
      setSending(true);

      // 委托适配器发送
      adapterRef.current.send({
        segments: msg.segments,
        senderId: currentUser.userId,
        senderName: currentUser.nickname,
        senderAvatar: currentUser.avatar,
      });

      // 400ms 后重置 sending 状态
      setTimeout(() => setSending(false), 400);
    },
    [currentUser]
  );

  // ==================== 重发消息 ====================

  /**
   * 重发失败的消息
   * 删除原消息，重新调用发送流程
   *
   * @param failedMessage 失败的消息
   */
  const retryMessage = useCallback(
    (failedMessage: GroupMessage) => {
      // 删除失败的消息
      setMessages((prev) => prev.filter((m) => m.id !== failedMessage.id));

      // 重新发送
      sendMessage({
        segments: failedMessage.segments,
      });
    },
    [sendMessage]
  );

  return {
    currentUser,
    showUserModal,
    messages,
    connectionStatus,
    sending,
    setUser,
    sendMessage,
    retryMessage,
  };
};
